/**
 * useChecklistTypes - Tipos de Checklist e filas globais para vistorias.
 *
 * @version 1.0.0
 * RELEASE: 2026-09-26 v1.0.0 - Criação: listagem/edição de tipos (RPCs list_checklist_types /
 *          upsert_checklist_type) e filas globais ativas (reaproveita
 *          get_distribution_queues_with_stats) para o responsável da vistoria.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useChecklistTypes');

export interface ChecklistType {
  id: string;
  name: string;
  description: string | null;
  color: string | null;
  is_active: boolean;
  sort_order: number;
  templates_count: number;
}

/** Lista os tipos do tenant ativo (somente ativos por padrão). */
export function useChecklistTypes(includeInactive = false, enabled = true) {
  const { currentTenant } = useAuth();
  return useQuery({
    queryKey: ['checklist-types', currentTenant?.id, includeInactive],
    enabled: enabled && !!currentTenant?.id,
    staleTime: 0,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('list_checklist_types' as never, {
        p_include_inactive: includeInactive, p_tenant_hint: null,
      } as never);
      if (error) { logger.error('Erro ao listar tipos', { error: error.message }); throw error; }
      return (data ?? []) as ChecklistType[];
    },
  });
}

/** Cria ou edita um tipo de checklist. */
export function useUpsertChecklistType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (a: { id?: string | null; data: Record<string, unknown> }) => {
      const { data, error } = await supabase.rpc('upsert_checklist_type' as never, {
        p_data: a.data, p_type_id: a.id ?? null, p_tenant_hint: null,
      } as never);
      if (error) throw error;
      return data as unknown as string;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['checklist-types'] }); toast.success('Tipo salvo'); },
    onError: (e: Error) => toast.error(e.message || 'Não foi possível salvar o tipo'),
  });
}

/** Filas de distribuição globais e ativas (responsável "Fila global"). */
export function useGlobalDistributionQueues(enabled: boolean) {
  const { currentTenant } = useAuth();
  return useQuery({
    queryKey: ['checklist-global-queues', currentTenant?.id],
    enabled: enabled && !!currentTenant?.id,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_distribution_queues_with_stats' as never, {
        p_tenant_hint: currentTenant?.id,
      } as never);
      if (error) throw error;
      return ((data ?? []) as Array<{ id: string; name: string; target_type: string; is_active: boolean }>)
        .filter((q) => q.target_type === 'global' && q.is_active);
    },
  });
}
